import { Seo } from "../components/Seo";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, MapPin, HelpCircle, Package, Headphones, Users } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { CONTACT_INFO } from "../data/products";
import { VG_ORANGE } from "../lib/brand";

const SECTIONS = [
  { key: "Customer Service", icon: Headphones, desc: "General customer service, account, and support inquiries.", productInterest: false, showProjectTitle: true },
  { key: "Sample Request", icon: Package, desc: "Request product samples — tell us what you'd like and where to ship them.", productInterest: true, formType: "request_samples", showShipping: true, showProjectTitle: true, requireProjectTitle: true },
  { key: "Sales Support", icon: Users, desc: "Connect with our sales team about volume, contracts, and partnerships.", productInterest: true, showProjectTitle: true },
  { key: "General Questions", icon: HelpCircle, desc: "Have a general question? Send it our way and we'll route it to the right person.", productInterest: false, showProjectTitle: true },
];

const topicSlug = (t) => t.replace(/\s+/g, "-").toLowerCase();

export default function Contact() {
  const [searchParams] = useSearchParams();
  const topicParam = searchParams.get("topic");
  const [active, setActive] = useState(
    () => SECTIONS.find((s) => topicSlug(s.key) === topicParam)?.key || SECTIONS[0].key
  );

  useEffect(() => {
    const match = SECTIONS.find((s) => topicSlug(s.key) === topicParam);
    if (match) setActive(match.key);
  }, [topicParam]);

  const section = SECTIONS.find((s) => s.key === active);

  return (
    <div data-testid="contact-page">
      <Seo title="Contact Us" description="Contact van Gelder, Inc. for commercial flooring sales, customer service, sample requests, and project support. Reach our team for entrance matting and specialty flooring inquiries." />
      <PageHero
        title="Contact Us"
        subtitle="Choose a topic below and send us a message. Our team routes every inquiry to the right department so you get help quickly."
        testId="contact-hero"
      />

      {/* Quick contact bar */}
      <section className="border-b border-neutral-200 bg-neutral-50">
        <div className="mx-auto max-w-7xl px-4 py-6 grid gap-4 sm:grid-cols-2">
          <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-3 rounded-lg bg-white border border-neutral-200 px-5 py-4 hover:border-[#ED8B00]" data-testid="contact-phone">
            <span className="flex h-11 w-11 items-center justify-center rounded-full text-white" style={{ backgroundColor: VG_ORANGE }}>
              <Phone className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs text-neutral-500">Call us</p>
              <p className="font-heading font-bold text-neutral-900">{CONTACT_INFO.phone}</p>
            </div>
          </a>
          <div className="flex items-center gap-3 rounded-lg bg-white border border-neutral-200 px-5 py-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full text-white" style={{ backgroundColor: VG_ORANGE }}>
              <MapPin className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs text-neutral-500">Visit us</p>
              <p className="font-heading font-bold text-neutral-900 text-sm">{CONTACT_INFO.address}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 grid gap-8 lg:grid-cols-[320px_1fr]">
        {/* Section selector */}
        <aside data-testid="contact-sections">
          <h2 className="font-heading text-lg font-bold text-neutral-900 mb-3">How can we help?</h2>
          <div className="flex flex-col gap-2">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.key === active;
              return (
                <button
                  key={s.key}
                  data-testid={`contact-section-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setActive(s.key)}
                  className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                    isActive
                      ? "border-[#ED8B00] bg-orange-50 text-neutral-900"
                      : "border-neutral-200 text-neutral-600 hover:border-neutral-300"
                  }`}
                >
                  <Icon className="h-5 w-5" style={{ color: isActive ? VG_ORANGE : "#9ca3af" }} />
                  {s.key}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Active form */}
        <div className="rounded-xl border border-neutral-200 bg-white p-6 md:p-8 shadow-sm">
          <h3 className="font-heading text-2xl font-extrabold text-neutral-900">{section.key}</h3>
          <p className="mt-1 mb-6 text-neutral-600">{section.desc}</p>
          <ContactForm
            key={section.key}
            department={section.key}
            defaultReason={section.key}
            showProductInterest={section.productInterest}
            formType={section.formType || "contact"}
            showShipping={!!section.showShipping}
            showProjectTitle={!!section.showProjectTitle}
            requireProjectTitle={!!section.requireProjectTitle}
            testId="contact-form"
          />
        </div>
      </section>
    </div>
  );
}
